import { lazy, Suspense, useState } from "react";
import { withStaleChunkRecovery } from "@/lib/lazy-with-reload";
import { SITE_URL } from "@/lib/constants";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
// N9-v2: preload target chunk on hover/focus. Drop-in for <Link>.
import { PreloadLink as Link } from "@/components/PreloadLink";
import Breadcrumb from "@/components/layout/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { productFamilies, productGroups, productLines, products, collapsedProductOfGroup } from "@/lib/data";
import { useDataVersion } from "@/hooks/useDataVersion";
import { getReplacementUrl } from "@/lib/productUrl";
import { whatsappProductUrl } from "@/lib/whatsapp";
import { MessageCircle, Package } from "lucide-react";
import HeroAccent from "@/components/ui/HeroAccent";
import CTASection from "@/components/home/CTASection";
import ProductOverview from "@/components/products/ProductOverview";
import ResumoSection from "@/components/products/ResumoSection";
import { willRenderSpecTable } from "@/lib/spec-table-presence";

// Every lazy import on this page goes through withStaleChunkRecovery: a
// tab held open across a deploy asks for chunk hashes that no longer
// exist, and an unwrapped 404 surfaces in the app-level ErrorBoundary as
// "Algo deu errado" over the entire page. See lib/lazy-with-reload.
//
// ProductMediaGallery (522 LOC) sits below the hero — usually
// just out of fold on mobile, fully out of fold on desktop where
// the hero takes the right column. Lazy with a height-reserving
// fallback so CLS stays at 0.
const ProductMediaGallery = lazy(withStaleChunkRecovery(() => import("@/components/products/ProductMediaGallery")));
// ModelSpecTable (640 LOC, biggest non-route component) is the
// main spec-data renderer. Always below the hero + gallery, so
// always out of LCP path. Lazy.
const ModelSpecTable = lazy(withStaleChunkRecovery(() => import("@/components/products/ModelSpecTable")));

// Below-the-fold sections — split into their own chunks. The
// ProductDetail page chunk was 268 KB raw (mostly because it
// eagerly imported these 5 components plus their transitive
// trees). Lazy-loading shifts ~150-300 KB of unused JS off
// the initial page load. Each section's chunk is fetched when
// React.lazy resolves the component, which happens when this
// page renders — but Vite's build splits each into its own
// chunk in dist/assets so the parser/compiler cost is paid
// only after first paint.
//
// Suspense fallback: a height-reserving spacer matching the
// section's typical rendered height, to prevent layout shift
// (CLS) while the chunk is in flight. The user only sees these
// fallbacks on a slow network — fast networks resolve the
// chunk before the user scrolls past the spec table.
const ToolCompatibilityTable = lazy(withStaleChunkRecovery(() => import("@/components/products/ToolCompatibilityTable")));
const CompatibilitySection = lazy(withStaleChunkRecovery(() => import("@/components/products/CompatibilitySection")));
const AccessoriesSection = lazy(withStaleChunkRecovery(() => import("@/components/products/AccessoriesSection")));
const SupportResourcesSection = lazy(withStaleChunkRecovery(() => import("@/components/products/SupportResourcesSection")));
const CrossSellSection = lazy(withStaleChunkRecovery(() => import("@/components/products/CrossSellSection")));

/**
 * Params normally come from the router (useParams). CMS Live Preview renders
 * this page OUTSIDE its real route (inside /preview/:collection), so it passes
 * the resolved slugs explicitly via `paramsOverride` instead — react-router
 * won't let a nested <Routes location> jump to an unrelated path, so we bypass
 * the router for the param source only. See pages/Preview.tsx.
 */
export type ProductDetailParams = {
  lineSlug?: string;
  familySlug?: string;
  groupSlug?: string;
  productSku?: string;
};

const ProductDetail = ({ paramsOverride }: { paramsOverride?: ProductDetailParams } = {}) => {
  const routeParams = useParams();
  const { lineSlug, familySlug, groupSlug, productSku } = paramsOverride ?? routeParams;
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);

  // Subscribe to data version so the page re-renders when the
  // background CMS refresh in initializeData() resolves with newer
  // data (e.g. an editor saved a spec_table after the SSR snapshot
  // was built). Without this hook, the lookups below (productLines,
  // productGroups, products, …) capture module state at first render
  // and stale fallback content keeps showing even after data.ts
  // overwrites the arrays. The bug surfaced specifically when an
  // editor populated a product's `specTable` and the new flat data
  // never replaced the legacy ModelSpecTable fallback on the page.
  useDataVersion();

  const line = productLines.find(l => l.slug === lineSlug);
  const family = productFamilies.find(f => f.slug === familySlug && f.lineId === line?.id);
  const group = productGroups.find(g => g.slug === groupSlug && g.familyId === family?.id);
  // The route's last segment is the product id (kebab slug). SKU was
  // dropped, so a single id-equality check is enough.
  const product = products.find(p => p.groupId === group?.id && p.id === productSku);


  const handleQuoteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Orçamento solicitado!",
      description: `Seu pedido para ${product?.name} foi enviado. Entraremos em contato em breve.`,
    });
    setQuoteDialogOpen(false);
  };

  if (!line || !family || !group || !product) {
    return (
      <div className="container-industrial section-padding text-center">
        <h1 className="text-2xl font-bold">Produto não encontrado</h1>
        <Link to="/produtos" className="text-primary hover:underline">
          Voltar para produtos
        </Link>
      </div>
    );
  }

  // Does this product actually have a variants table? Decides where the
  // "Especificações revisadas em …" line lands. Same predicate the
  // component uses for its own early return, so the two can't drift.
  const specTableRenders = willRenderSpecTable(product.specTable);

  // SITE_URL imported at top of file
  const canonicalPath = `/produtos/${line.slug}/${family.slug}/${group.slug}/${product.id}`;
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  // SEO overrides set per-product in the CMS take precedence; fall back
  // to the auto-built title and the long-form description otherwise.
  // (Used to read `${product.name} | ${product.sku} - Or Brasil` but
  // SKU was dropped — name alone is unique enough within the catalogue.)
  const pageTitle = product.metaTitle ?? `${product.name} - Or Brasil`;
  const pageDescription = product.metaDescription ?? product.description;

  // Map every non-empty spec onto a PropertyValue. The label set is
  // PT-BR so Google's "Specifications" rich-result panel reads natural
  // to the Brazilian buyer. Adding new spec keys to ProductSpecs?
  // Add a label here too; falls back to a humanised key if missing.
  const SPEC_LABELS_PT: Record<string, string> = {
    material: "Material",
    bodyFormat: "Formato",
    headType: "Tipo de Cabeça",
    bottomType: "Tipo de Base",
    diameter: "Diâmetro",
    length: "Comprimento",
    type: "Tipo",
    rivetableThickness: "Espessura Rebitável",
    application: "Aplicação",
    weight: "Peso",
    finish: "Acabamento",
    tensileStrength: "Resistência à Tração",
    shearStrength: "Resistência ao Cisalhamento",
    typeFilter: "Tipo de Rebite",
    tractionForce: "Força de Tração",
    noise: "Ruído",
    charger: "Carregador",
    battery: "Bateria",
    rechargeTime: "Tempo de Recarga",
    airPressure: "Pressão do Ar",
    stroke: "Curso",
    capacity: "Capacidade",
    operatingPressure: "Pressão de Trabalho",
    minAirTubeDiameter: "Ø Mínimo Mangueira",
    airConsumptionPerCycle: "Consumo de Ar / Ciclo",
  };
  const humanise = (k: string) => k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
  const additionalProperty = Object.entries(product.specs)
    .filter(([, v]) => typeof v === "string" && v.trim().length > 0)
    .map(([k, v]) => ({
      "@type": "PropertyValue",
      name: SPEC_LABELS_PT[k] ?? humanise(k),
      value: v,
    }));

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    // sku/productID/mpn fall back to the kebab id so structured data
    // still has a stable identifier for Google. Product-level SKU was
    // dropped — the per-row códigos in the spec table are the real
    // purchasable identifiers and they don't fit Schema.org's single-
    // valued sku field at the Product level.
    sku: product.id,
    productID: product.id,
    mpn: product.id,
    description: product.description,
    image: product.image ? `${SITE_URL}${product.image}` : undefined,
    brand: { "@type": "Brand", name: "Or Brasil" },
    manufacturer: { "@type": "Organization", name: "Or Brasil" },
    url: canonicalUrl,
    category: `${line.name} > ${family.name} > ${group.name}`,
    material: product.specs.material || undefined,
    inLanguage: "pt-BR",
    audience: { "@type": "BusinessAudience", audienceType: "Indústria" },
    // Same CMS timestamp the page shows under the spec table. Recent
    // timestamps are a documented (if marginal) AI-citation signal, and
    // it costs nothing — Payload already stamps every save.
    // No `offers` here on purpose: no prices are published, and a
    // price-less Offer earns no rich result while risking a Search
    // Console warning. Pending owner decision (AEO-PROPOSAL.md Q2).
    dateModified: product.updatedAt ?? undefined,
    additionalProperty,
  };

  // Drop the group crumb when it's redundant: either the group shares the
  // family's slug, or this product is the sole product of a collapsed
  // single-product group (group name == product name), whose group page
  // just redirects back here. See collapsedProductOfGroup().
  const skipGroup = group.slug === family.slug || collapsedProductOfGroup(group.id)?.id === product.id;
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Início", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: line.name, item: `${SITE_URL}/produtos/${line.slug}` },
      { "@type": "ListItem", position: 3, name: family.name, item: `${SITE_URL}/produtos/${line.slug}/${family.slug}` },
      ...(skipGroup ? [] : [{ "@type": "ListItem", position: 4, name: group.name, item: `${SITE_URL}/produtos/${line.slug}/${family.slug}/${group.slug}` }]),
      { "@type": "ListItem", position: skipGroup ? 4 : 5, name: product.name, item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Or Brasil" />
        <meta property="og:image" content={`${SITE_URL}${product.image}`} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${SITE_URL}${product.image}`} />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Discontinued banner — sticky BELOW the site Header. The
          Header itself is sticky top-0 z-50; if this banner used
          top-0 it would sit underneath the header backdrop. We
          offset by --site-header-h (set on the Header on mount) so
          the banner stays visible while scrolling and never
          collides with the header. Falls back to 64px when the
          CSS var isn't set yet (SSR / first paint). */}
      {product.discontinued && (
        <section
          className="sticky z-[45] bg-amber-500 text-amber-50 shadow-md"
          style={{ top: "var(--site-header-h, 64px)" }}
        >
          <div className="container-industrial py-3 md:py-3.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
            <span className="inline-flex items-center gap-2 text-sm md:text-base font-semibold">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 text-xs font-bold uppercase tracking-wide">
                Produto Descontinuado
              </span>
              <span>
                Disponibilizamos apenas peças de reposição e assistência técnica.
              </span>
            </span>
            {(() => {
              const replacementUrl = getReplacementUrl(product.discontinuedReplacement);
              if (!replacementUrl || !product.discontinuedReplacement) return null;
              return (
                <Link
                  to={replacementUrl}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-amber-50 text-amber-900 text-sm md:text-base font-semibold hover:bg-white transition-colors"
                >
                  Substituído por {product.discontinuedReplacement.name} →
                </Link>
              );
            })()}
          </div>
        </section>
      )}

      {/* 1) Hero — dark, left-aligned, matching site pattern */}
      <section className="relative min-h-[380px] lg:min-h-[440px] flex items-center overflow-hidden bg-surface-dark">
        <div className="absolute inset-0 opacity-[0.02] bg-grid-fade" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-primary/8 rounded-full blur-[140px]" aria-hidden="true" />

        {/* Desktop hero accent — render the CMS hero_image when one
            is set, otherwise fall back to the Package placeholder.
            The circular crop matches the brand's existing pattern;
            FadeImage advertises retina sources via `sizes`.
            z-20 is intentional and necessary: the sibling
            `container-industrial` div below has `z-10` and is
            `w-full`, so its empty right-hand half visually overlaps
            this hero accent. macOS Safari's mouse hit-test
            respects strict stacking order and routes every click
            on the visible model to that empty content div instead
            of to the hero accent — the long-running 3D drag bug
            had nothing to do with the viewer code itself. Bumping
            the accent above the content fixes it. */}
        {/* Pulled inward (+6rem past the container edge) and enlarged
            to 384px so the product anchors the right half instead of
            floating in the far corner — closes the dead center gap on
            wide screens (feedback #7, "Option A"). */}
        <div className="hidden lg:flex absolute right-[max(2rem,calc((100vw-80rem)/2+6rem))] top-1/2 -translate-y-1/2 z-20">
          <div className="relative w-96 h-96 rounded-full overflow-hidden bg-surface-dark-elevated border border-surface-dark-hover flex items-center justify-center">
            <HeroAccent
              modelUrl={product.hero3dModelUrl}
              src={product.image}
              imageSources={product.imageSources}
              alt={product.name}
              sizes="384px"
              breakpoint="desktop"
              placeholder={<Package className="w-24 h-24 text-on-surface-dark-faint" />}
            />
          </div>
        </div>

        <div className="w-full container-industrial relative z-10 py-8 md:py-16 lg:py-20">
          <div className="max-w-xl lg:max-w-[50%]">
            <Breadcrumb
              items={[
                { label: line.name, href: `/produtos/${line.slug}` },
                { label: family.name, href: `/produtos/${line.slug}/${family.slug}` },
                ...(!skipGroup ? [{ label: group.name, href: `/produtos/${line.slug}/${family.slug}/${group.slug}` }] : []),
                { label: product.name },
              ]}
            />


            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.1] mb-4 animate-slide-up text-white">
              {product.name}
            </h1>

            <p
              className="text-lg text-on-surface-dark-muted max-w-2xl animate-slide-up leading-relaxed mb-6 [animation-delay:100ms]"
            >
              {product.description}
            </p>

            {/* Industry tags — surface the editor's "for which sectors"
                selection as small chips. Using neutral white-on-dark
                pills (no colored stripe / no rounded-icon row) so they
                read as metadata rather than primary actions. */}
            {product.industrySlugs && product.industrySlugs.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-10 animate-slide-up [animation-delay:120ms]">
                {product.industrySlugs.map((ind) => (
                  <span
                    key={ind.slug}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-on-surface-dark-muted bg-white/5 border border-white/15"
                  >
                    {ind.name}
                  </span>
                ))}
              </div>
            )}

            {/* Mobile hero accent — same image-or-fallback pattern as
                desktop above; smaller circle since it sits centered
                above the CTA buttons on phones.

                NO animate-slide-up here. This is the LCP element on
                the mobile viewport (Lighthouse's reference profile);
                the slide-up keyframes go opacity 0 → 1 over 600ms +
                150ms delay, and Chrome's LCP heuristic ignores
                opacity-0 elements. On throttled mobile, the animation
                pipeline pushed LCP out to ~11s. Removing the
                animation lets the image paint immediately.
                Investigated 2026-05-14. */}
            <div className="flex items-center justify-center lg:hidden mb-10">
              <div className="relative w-72 h-72 sm:w-80 sm:h-80 rounded-full overflow-hidden bg-surface-dark-elevated border border-surface-dark-hover flex items-center justify-center">
                <HeroAccent
                  modelUrl={product.hero3dModelUrl}
                  src={product.image}
                  imageSources={product.imageSources}
                  alt={product.name}
                  sizes="(min-width: 640px) 320px, 288px"
                  breakpoint="mobile"
                  placeholder={<Package className="w-16 h-16 text-on-surface-dark-faint" />}
                />
              </div>
            </div>

            <div
              className="flex flex-col sm:flex-row gap-3 animate-slide-up [animation-delay:200ms]"
            >
              <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="hero" className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                    Solicitar Orçamento
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Solicitar Orçamento — {product.name}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    <Input placeholder="Nome completo *" required />
                    <Input placeholder="E-mail corporativo *" type="email" required />
                    <Input placeholder="Telefone com DDD" />
                    <Input placeholder="Empresa" />
                    <Input placeholder="Quantidade desejada" type="number" min={1} />
                    <Textarea placeholder="Observações técnicas" rows={3} />
                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                      Enviar Solicitação
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <a
                href={whatsappProductUrl(product.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button size="hero" variant="whatsapp" className="w-full sm:w-auto">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  WhatsApp
                </Button>
              </a>

              <Link to={`/comparador?p=${product.id}`} className="w-full sm:w-auto">
                {/* Match the size + radius of the other two pills
                    (Solicitar Orçamento, WhatsApp). The previous
                    size="lg" + manual py-6 override made this pill
                    ~6px taller than the others. */}
                <Button
                  size="hero"
                  className="w-full sm:w-auto border border-white/25 bg-white/5 text-white hover:bg-white/10"
                >
                  Comparar
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Resumo — the lead paragraph, deliberately OUTSIDE `.product-bands`.
          That wrapper stripes its direct children by nth-of-type, so a
          section added inside it would invert every band below. Sitting
          above it with a fixed muted background keeps the alternation
          (dark hero, muted, light, muted, …) and leaves every existing
          band exactly where it was. */}
      <ResumoSection resumo={product.resumo} />

      {/* Auto-banded body sections — one wrapper alternates each child
          <section>'s background (light / muted) via CSS nth-of-type, so
          two adjacent sections never share a background even as optional
          sections (specs, compatibility, accessories) come and go per
          product. No per-section bg hardcoding. See `.product-bands` in
          index.css. */}
      <div className="product-bands">
      {/* 2) Media Gallery — editorial style. Lazy: 522 LOC + a
          <Suspense> for Model3DViewer; below-fold on most viewports. */}
      <Suspense fallback={<div className="min-h-[600px]" aria-hidden="true" />}>
        <ProductMediaGallery
          media={product.media}
          productName={product.name}
          intro={product.introText}
          introTitle={product.introTitle}
          datasheetUrl={product.datasheet}
          drawingUrl={product.drawing}
        />
      </Suspense>

      {/* 3) Characteristics table. The review date goes under the table
          it refers to: the variants table when this product has one,
          otherwise this one, which is then the only spec table on the
          page. Only 37 of 84 products have a variants table, so without
          the fallback the date would be unreachable on the other 47. */}
      <ProductOverview
        product={product}
        revisedAt={specTableRenders ? undefined : product.specsRevisadasEm}
      />

      {/* Spec / variant tables. Both can render simultaneously —
          ModelSpecTable is the authored Lovable-derived
          "Tabela de Especificações" (with the mobile tap-to-expand,
          search-by-code, and other improvements layered on top of
          the original Lovable version), driven by spec-code-index
          for products whose SKU has a registered model. The
          ToolCompatibilityTable is the editor-controlled
          compatibility grid from the CMS. The previous XOR was
          wrong — products that have BOTH a compatibility matrix
          AND a spec-code table need to surface both. */}
      <Suspense fallback={<div className="min-h-[800px]" aria-hidden="true" />}>
        <ModelSpecTable
          modelSlug={productSku}
          data={product.specTable}
          image={product.specTableImage}
          revisedAt={specTableRenders ? product.specsRevisadasEm : undefined}
        />
      </Suspense>
      {product.compatibilityMatrix && (
        <Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}>
          <ToolCompatibilityTable matrix={product.compatibilityMatrix} productName={product.name} />
        </Suspense>
      )}

      {/* Suporte & Manutenção */}
      {/* Suporte & Manutenção — videos, tips, and the "Precisa
          de mais ajuda?" CTA are all per-product. Component reads
          from the Product fields with sensible global fallbacks. */}
      <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
        <SupportResourcesSection product={product} />
      </Suspense>

      {/* Compatibility — CMS-fed (Products.compatibility array).
          Returns null when nothing is mapped, so an unset record
          simply omits the section instead of falling through to
          the old hardcoded list.

          Keyed by product: navigating product → product stays on the
          same route, so React keeps this instance mounted and its
          "Ver todas" open state leaked onto the next product's page.
          The key forces a remount, which resets it. */}
      <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
        <CompatibilitySection key={product.id} product={product} />
      </Suspense>

      {/* CMS-driven cross-sell — acessórios. "Produtos relacionados"
          was removed from the page (the editor was duplicating the
          same products into multiple cross-sell buckets and the
          distinction wasn't meaningful to readers). Acessórios e
          bicos comes from Products.accessories (relationship field
          in the CMS sidebar). Placed after "Rebitadeiras Compatíveis"
          (the CompatibilitySection above). */}
      <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
        {product.accessorySlugs && product.accessorySlugs.length > 0 ? (
          <CrossSellSection
            title="Acessórios e bicos"
            subtitle="Bicos e acessórios para maximizar o desempenho"
            items={product.accessorySlugs}
          />
        ) : (
          /* Legacy static fallback when no CMS accessories are wired.
             Keyed for the same reason as CompatibilitySection above —
             it carries its own "Ver mais acessórios" open state. */
          <AccessoriesSection key={product.id} productId={product.id} groupId={group.id} />
        )}
      </Suspense>

      </div>

      {/* Fixed pre-footer CTA */}
      <CTASection />
    </>
  );
};

export default ProductDetail;
